import {Flex, Paper, Text, Transition} from "@mantine/core";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useAppSelector} from "../../../app/hooks";
import {selectActiveStreamCount} from "../threadsSlice";
import {CommandInput} from "./CommandInput";
import {useTriggerWordDetection} from "../hooks/useTriggerWordDetection";
import classes from "./SplashInput.module.css";
import {
    PendingPasteAttachment,
    SubmitWithAttachments,
} from "../utils/pasteAttachments";

const MAX_CONCURRENT_STREAMS = 3;
const POPUP_VISIBLE_MS = 3500;

interface SplashInputProps {
    onSendMessage: SubmitWithAttachments;
    onValueChange: (value: string) => void;
    value?: string;
}

export const SplashInput = ({
    onSendMessage,
    onValueChange,
    value,
}: SplashInputProps) => {
    const {t} = useTranslation();
    const {isTriggerSearch} = useTriggerWordDetection(value || "");
    const activeStreamCount = useAppSelector(selectActiveStreamCount);
    const atCapacity = activeStreamCount >= MAX_CONCURRENT_STREAMS;

    const [showCapacityPopup, setShowCapacityPopup] = useState(false);
    const hideTimeoutRef = useRef<number | null>(null);

    const handleSubmit = (
        _message: string,
        attachments: PendingPasteAttachment[]
    ) => {
        if (!value || value.trim() === "") {
            return false;
        }
        if (onSendMessage(value, attachments) === false) {
            return false;
        }
        onValueChange("");
        return true;
    };

    const handleBlockedSubmit = useCallback(() => {
        setShowCapacityPopup(true);
        if (hideTimeoutRef.current) {
            window.clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = window.setTimeout(() => {
            setShowCapacityPopup(false);
            hideTimeoutRef.current = null;
        }, POPUP_VISIBLE_MS);
    }, []);

    useEffect(() => {
        if (!atCapacity && showCapacityPopup) {
            setShowCapacityPopup(false);
        }
    }, [atCapacity, showCapacityPopup]);

    useEffect(
        () => () => {
            if (hideTimeoutRef.current) {
                window.clearTimeout(hideTimeoutRef.current);
            }
        },
        []
    );

    return (
        <Flex direction="column" pos="relative">
            <Transition
                mounted={showCapacityPopup}
                transition="pop-bottom-left"
                duration={180}
                timingFunction="ease-out"
            >
                {(styles) => (
                    <Paper className={classes.capacityPopup} style={styles}>
                        <Text className={classes.capacityTitle}>
                            {t("chat.multi_stream.capacity_title", {
                                defaultValue: "Too many active conversations",
                            })}
                        </Text>
                        <Text className={classes.capacityMessage}>
                            {t("chat.multi_stream.capacity_message", {
                                defaultValue:
                                    "3 conversations are already in progress. Please wait for one to finish.",
                            })}
                        </Text>
                    </Paper>
                )}
            </Transition>
            <CommandInput
                variant="splash"
                disabled={false}
                onChange={onValueChange}
                onSendMessage={handleSubmit}
                isTriggerSearch={isTriggerSearch}
                atCapacity={atCapacity}
                onBlockedSubmit={handleBlockedSubmit}
            />
        </Flex>
    );
};
